// src/pages/health/detail/index.js
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import AuthCard from "../../../components/auth/AuthCard";
import BottomNav from "../../../components/common/BottomNav";
import HealthCareModal from "../components/healthCareModal";
import { useLoadingGate } from "../../../hooks/useLoadingGate";
import {
    useDeleteBodyFat,
    useDeleteBp, useDeleteGlucose, useDeleteWeight,
    useUpdateBodyFat,
    useUpdateBp, useUpdateGlucose, useUpdateWeight
} from "../../../hooks/useHealthMetrics";

const getBodyFatMass = (r) =>
    r?.body_fat ?? r?.body_fat_mass ?? r?.bodyFatMass ?? r?.fat_mass ?? r?.bodyFat ?? r?.value;

const METAS = {
    bp: {
        title: "수축 / 이완 혈압 기록",
        color: "#ED6363",
        formatMain: (r) => `${r.sys} / ${r.dia}`,
        modalVariant: "bp",
        useUpdate: useUpdateBp,
        useDelete: useDeleteBp,
        initialFromReading: (r) => ({ sys: r.sys, dia: r.dia, note: r.note }),
        submitMap: ({ payload, reading }) => ({
            originalDt: reading.dt,
            dt: payload.dt,
            sys: payload.sys,
            dia: payload.dia,
            note: payload.note,
        }),
        deleteMap: (reading) => ({ dt: reading.dt }),
    },
    weight: {
        title: "체중 기록",
        color: "#7EDBB6",
        formatMain: (r) => `${r.weight} kg`,
        modalVariant: "weight",
        useUpdate: useUpdateWeight,
        useDelete: useDeleteWeight,
        initialFromReading: (r) => ({ weight: r.weight, note: r.note }),
        submitMap: ({ payload, reading }) => ({
            originalDt: reading.dt,
            dt: payload.dt,
            weight: payload.weight,
            note: payload.note,
        }),
        deleteMap: (reading) => ({ dt: reading.dt }),
    },
    "body-fat": {
        title: "체지방량 기록",
        color: "#B48CF2",
        formatMain: (r) => `${getBodyFatMass(r) ?? "-"} kg`,
        modalVariant: "bodyFat",
        useUpdate: useUpdateBodyFat,
        useDelete: useDeleteBodyFat,
        initialFromReading: (r) => ({
            body_fat: getBodyFatMass(r),
            note: r.note,
        }),
        submitMap: ({ payload, reading }) => ({
            originalDt: reading.dt,
            dt: payload.dt,
            body_fat: payload.body_fat,
            note: payload.note,
        }),
        deleteMap: (reading) => ({ dt: reading.dt }),
    },
    glucose: {
        title: "혈당 기록",
        color: "#E9D045",
        formatMain: (r) => `${r.fasting} mg/dL`,
        modalVariant: "glucose",
        useUpdate: useUpdateGlucose,
        useDelete: useDeleteGlucose,
        initialFromReading: (r) => ({ fasting: r.fasting, note: r.note }),
        submitMap: ({ payload, reading }) => ({
            originalDt: reading.dt,
            dt: payload.dt,
            fasting: payload.fasting,
            note: payload.note,
        }),
        deleteMap: (reading) => ({ dt: reading.dt }),
    },
};

export default function HealthCareDetail() {
    const ready = useLoadingGate();
    const navigate = useNavigate();
    const { state } = useLocation();

    const metricKey = state?.metric ?? "bp";
    const meta = METAS[metricKey] ?? METAS.bp;

    const [readingLocal, setReadingLocal] = useState(state?.reading ?? null);
    const [openEdit, setOpenEdit] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);

    const { mutate: updateMutate, isPending: updating } = meta.useUpdate();
    const { mutateAsync: deleteMutateAsync, isPending: deleting } = meta.useDelete();

    useEffect(() => {
        if (!readingLocal) navigate(-1);
    }, [readingLocal, navigate]);

    if (!readingLocal) return null;

    const dt = new Date(readingLocal.dt);

    const handleSubmitEdit = (payload) => {
        const mapped = meta.submitMap({ payload, reading: readingLocal });
        updateMutate(mapped, {
            onSuccess: (updated) => {
                setReadingLocal(updated);
                setOpenEdit(false);
            },
            onError: (err) => window.alert(err?.message || "수정에 실패했습니다."),
        });
    };

    const handleConfirmDelete = async () => {
        try {
            await deleteMutateAsync(meta.deleteMap(readingLocal));
            setOpenDelete(false);
            navigate(-1);
        } catch (err) {
            window.alert(err?.message || "삭제에 실패했습니다.");
        }
    };

    return (
        <main className="h-[var(--app-height)] flex flex-col font-suit overflow-y-auto scrollbar-hide px-7 bg-white">
            {!ready ? (
                <div className="flex-1" />
            ) : (
                <AuthCard
                    className="flex-1 w-full mx-auto"
                    title={format(dt, "yyyy-MM-dd")}
                    onClose={() => navigate(-1)}
                >
                    <div className="flex flex-col flex-1 pt-3 pb-24">
                        <div className="text-sm md:text-base text-gray-500 mb-3">
                            {meta.title}
                        </div>

                        <div className="rounded-2xl border border-gray-200 p-4 flex gap-4 items-center">
                            <div
                                className="w-6 md:w-7 h-24 md:h-28 rounded-lg"
                                style={{ backgroundColor: meta.color }}
                            />
                            <div className="flex-1 min-w-0 ml-auto text-right flex flex-col items-end space-y-2 md:space-y-3">
                                <div
                                    className="text-4xl md:text-5xl font-semibold tracking-tight"
                                    style={{ color: meta.color }}
                                >
                                    {meta.formatMain(readingLocal)}
                                </div>
                                <div className="text-gray-400 text-xs md:text-sm">
                                    {format(new Date(readingLocal.dt), "yyyy-MM-dd HH:mm:ss")}
                                </div>
                            </div>
                        </div>

                        <div className="mt-5">
                            <div className="text-sm md:text-base text-gray-500 mb-2">메모 사항</div>
                            <div className="rounded-2xl border border-gray-200 p-3 md:p-4 min-h-[300px] max-h-[45vh] overflow-y-auto text-sm md:text-base text-gray-700 leading-relaxed bg-white">
                                {readingLocal.note ? (
                                    <p className="whitespace-pre-wrap">{readingLocal.note}</p>
                                ) : (
                                    <p className="text-gray-400">메모가 없습니다.</p>
                                )}
                            </div>
                        </div>

                        <div className="mt-6 grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                className="h-12 rounded-xl font-semibold bg-[#C7C7C7] text-white active:scale-[0.99]"
                                onClick={() => setOpenEdit(true)}
                            >
                                기록 수정
                            </button>
                            <button
                                type="button"
                                className="h-12 rounded-xl font-semibold text-white active:scale-[0.99]"
                                style={{ backgroundColor: meta.color }}
                                onClick={() => setOpenDelete(true)}
                            >
                                기록 삭제
                            </button>
                        </div>
                    </div>
                </AuthCard>
            )}

            <HealthCareModal
                open={openEdit}
                mode="edit"
                variant={meta.modalVariant}
                date={new Date(readingLocal.dt)}
                initial={meta.initialFromReading(readingLocal)}
                confirmLabel="변경 내용 저장"
                busy={updating}
                onClose={() => setOpenEdit(false)}
                onSubmit={handleSubmitEdit}
            />

            {openDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                        onClick={() => (deleting ? null : setOpenDelete(false))}
                    />
                    <div className="relative z-10 w-[min(520px,92vw)] rounded-2xl bg-white shadow-xl">
                        <div className="px-6 pt-5 pb-2 flex items-center justify-center">
                            <button
                                type="button"
                                aria-label="닫기"
                                onClick={() => setOpenDelete(false)}
                                disabled={deleting}
                                className="absolute right-3 top-3 p-2 rounded-full hover:bg-gray-100 active:scale-95"
                            >
                                <svg className="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none">
                                    <path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </button>
                            <h2 className="text-xl md:text-2xl font-extrabold">기록 삭제</h2>
                        </div>

                        <div className="px-6 pb-6">
                            <p className="text-center text-gray-700 mb-4">다음 기록을 삭제합니다.</p>
                            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-center font-medium text-gray-700">
                                {format(new Date(readingLocal.dt), "yyyy-MM-dd")}
                                <br />
                                {meta.formatMain(readingLocal)}
                            </div>

                            <div className="grid grid-cols-2 gap-3 mt-5">
                                <button
                                    type="button"
                                    onClick={() => setOpenDelete(false)}
                                    className="h-12 rounded-xl font-semibold bg-[#C7C7C7] text-white active:scale-[0.99]"
                                    disabled={deleting}
                                >
                                    취소
                                </button>
                                <button
                                    type="button"
                                    onClick={handleConfirmDelete}
                                    className="h-12 rounded-xl font-semibold text-white active:scale-[0.99] disabled:opacity-60"
                                    style={{ backgroundColor: meta.color }}
                                    disabled={deleting}
                                >
                                    {deleting ? "삭제 중..." : "삭제"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <BottomNav className="h-16" />
        </main>
    );
}
